import classNames from 'classnames';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import {
  Button,
  ErrorMessage,
  FlexContainer,
  Input,
  InputFile,
  Modal,
  Picture,
  Spinner,
  Txt,
  View,
  Icon,
} from 'components';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useProfile, useDataActions } from 'state';
import styles from './Messaging.module.scss';

const getMessageTitle = (data) => {
  return data[0].items[0].data.noticeTitle;
};

/**
 * Messaging
 *
 * @return {JSX.Element}
 */
const Messaging = ({ className, message, onClose }) => {
  const ref = useRef();
  const profileDoc = useProfile();
  const storageActions = useDataActions();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewImage, setViewImage] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const { data, tenancyUid } = message;
  const profileUid = profileDoc.data.profileUid;
  const messagesTitle = useMemo(() => getMessageTitle(data), [data]);
  // console.log(message);

  useEffect(() => {
    const unviewedItems = message.data.reduce((acc, group) => {
      const groupUnviewd = group.items.filter(
        (itemDoc) =>
          !itemDoc.data.hasViewed && itemDoc.data.messageFromUid !== profileUid
      );

      if (groupUnviewd.length) {
        acc.push(...groupUnviewd);
      }

      return acc;
    }, []);

    if (unviewedItems.length) {
      storageActions.notices.markAsViewed(unviewedItems);
    }
  }, [message, profileUid, storageActions.notices]);

  const handlePictureClick = (url) => () => setViewImage(url);
  const handleClosePictureView = () => setViewImage(false);

  const handleSubmitMessage = async (e) => {
    e.preventDefault();

    if (isLoading) return false;

    setNewMessage('');
    setIsLoading(true);
    // console.log('Submit')
    try {
      await storageActions.notices.sendMessage(
        tenancyUid,
        newMessage,
        messagesTitle
      );

      console.log(ref.current);
    } catch (e) {
      console.error(e);
      setError(e.message);
    }

    setIsLoading(false);
    // ref.current?.focus();
    // noticesState.sendMessage(newMessage, message, userState.profile.state);
  };

  const handleImage = async ({ target: { files } }) => {
    setError('');
    if (files && files[0]) {
      try {
        setIsLoading(true);
        await storageActions.notices.sendFile(
          tenancyUid,
          files[0],
          messagesTitle
        );
      } catch (e) {
        setError(e.message);
      }
      setIsLoading(false);
      ref.current?.focus();
    }
  };

  const handleNewMessageChange = ({ target: { value } }) => {
    setError('');
    setNewMessage(value);
  };

  return (
    <FlexContainer
      className={classNames(styles.Messaging, className, Messaging.displayName)}
      column
    >
      <FlexContainer alignCenter gap spaceBetween className={styles.Header}>
        <FlexContainer alignCenter gap>
          <Icon name="message" highlight="black" />
          <Txt xx>{messagesTitle}</Txt>
        </FlexContainer>
        {onClose ? <Icon name="close" onClick={onClose} /> : null}
      </FlexContainer>
      <FlexContainer column gap className={styles.Body}>
        <FlexContainer column gap className={styles.Scroll}>
          {data.map((group) => {
            const dateString = DateTime.fromJSDate(
              group.date?.toDate()
            ).toLocaleString(DateTime.DATE_SHORT);

            return (
              <Fragment key={dateString}>
                <FlexContainer
                  className={styles.MessageDate}
                  justifyContentCenter
                >
                  <View className={styles.MessageText}>
                    <Txt m>{dateString}</Txt>
                  </View>
                </FlexContainer>
                {group.items.map(({ data: item }) => (
                  <FlexContainer key={item.noticeUid} column gapHalf>
                    {item.messageFromUid === profileUid ? null : (
                      <Txt>{item.messageFromName}</Txt>
                    )}
                    <FlexContainer
                      className={classNames(
                        styles.MessageAligner,
                        item.messageFromUid === profileUid &&
                          styles.MessageAligner__own
                      )}
                    >
                      <FlexContainer
                        className={styles.MessageItem}
                        gapHalf
                        alignCenter
                      >
                        {item.temp ? <Spinner /> : null}
                        {item.isImage ? (
                          <Picture
                            src={item.messageText}
                            className={styles.MessagePicture}
                            onClick={handlePictureClick(item.messageText)}
                          />
                        ) : (
                          <View className={styles.MessageText}>
                            <Txt regular>{item.messageText}</Txt>
                          </View>
                        )}
                      </FlexContainer>
                    </FlexContainer>
                  </FlexContainer>
                ))}
              </Fragment>
            );
          })}
        </FlexContainer>
      </FlexContainer>
      <form onSubmit={handleSubmitMessage}>
        <FlexContainer gap className={styles.Footer}>
          <InputFile
            onChange={handleImage}
            accept="image/jpeg,image/png"
            disabled={isLoading}
          >
            <Button reverse className={styles.ImagePickerButton}>
              <Icon name="plus" />
            </Button>
          </InputFile>

          <FlexContainer flex>
            <Input
              fullWidth
              ref={ref}
              value={newMessage}
              onChange={handleNewMessageChange}
              autoFocus
            />
          </FlexContainer>
          <Button success type="submit" disabled={isLoading}>
            Send
          </Button>
        </FlexContainer>
        {error ? <ErrorMessage small description={error} /> : null}
      </form>
      {viewImage ? (
        <Modal
          onClose={handleClosePictureView}
          noPadding
          title="Picture"
          showCloseButton
          fullHeight={false}
        >
          <FlexContainer>
            <Picture src={viewImage} />
          </FlexContainer>
        </Modal>
      ) : null}
    </FlexContainer>
  );
};

Messaging.displayName = 'Messaging';

export default observer(Messaging);
